import React, { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import NoticePopup from './NoticePopup';

interface RequiredNoticeGuardProps {
  children: ReactNode;
}

/**
 * 관문을 적용하지 않는 경로.
 * 정책(마켓홈)과 순환 방지(로그인·상점약관·QR 역매핑)를 위한 예외다.
 * ProviderRouter.tsx 의 실제 path 와 1:1 대응하므로, 라우트를 추가/변경하면 여기도 함께 본다.
 */
function isExemptPath(pathname: string): boolean {
  // 마켓홈 — md 확정 면제(비로그인도 보는 곳, 필수 공지 안 띄움).
  if (pathname === '/' || pathname === '') return true;

  // 로그인 계열 — 관문이 걸리면 로그인 자체가 불가능해진다.
  //  ProviderRouter: '/login'(:312) '/login/:storeNo'(:389)
  //                  '/login/oauth2/code/kakao'(:365) '/login/oauth2/code/naver'(:373)
  if (pathname === '/login' || pathname.startsWith('/login/')) return true;
  // ★'/auth/google/callback'(:381)은 경로에 login 문자열이 없다 — 접두사 필터로 못 잡히므로 별도 처리.
  if (pathname.startsWith('/auth/')) return true;

  // QR 역매핑/랜딩 — 여기서 막으면 목적지 판정 전에 팝업이 떠 원래 목적지가 유실된다.
  //  역매핑 후 목적지 화면에서 관문이 잡으므로 우회 경로가 되지 않는다.
  //  ProviderRouter: '/app/qr/:type/:uuid'(:230) '/app/link/:type/:uuid'(:239)
  if (pathname.startsWith('/app/')) return true;

  // 상점 약관 동의 — 관문이 걸리면 ConsentGuard.tsx:52 가 보낸 목적지에 도달할 수 없어
  //  상점 약관 동의가 불가능해진다. ProviderRouter: '/:storeNo/consent'(:397)
  if (/^\/[^/]+\/consent\/?$/.test(pathname)) return true;

  // 마켓 약관·방침 전문 — TermsGuard 와 같은 목록을 유지한다(TermsGuard.tsx:14-16).
  //  약관 관문의 [보기]로 여는 문서 위에 공지 팝업이 겹치는 것도 막는다.
  //  ProviderRouter: '/naegift-app-terms' '/naegift-app-privacy'
  if (
    pathname === '/naegift-app-terms' ||
    pathname === '/naegift-app-privacy'
  ) {
    return true;
  }

  return false;
}

/**
 * 미동의 필수 공지 관문.
 * ProviderRouter.tsx 의 path:'/' 그룹 element 한 곳에 적용되어 그 하위 전 라우트를 커버한다
 * (storeNo 계열 공통 부모가 없어 개별 적용은 15곳 중복이 된다).
 *
 * 조회·팝업·동의 저장·통과 판정은 NoticePopup(mode='required')이 담당한다.
 * 비로그인은 서버가 requiredNotices=[] 를 주므로(user-notice.controller @Public) 그대로 통과한다.
 *
 * ★여기서 import 하는 './NoticePopup' 의 default export 는 NoticePopupBoundary 다.
 *   렌더 예외 시 children 을 통과시켜(fail-open) 페이지가 백지가 되지 않는다.
 */
export default function RequiredNoticeGuard({
  children,
}: RequiredNoticeGuardProps): React.JSX.Element {
  const { pathname } = useLocation();
  if (isExemptPath(pathname)) {
    return <>{children}</>;
  }
  return <NoticePopup mode="required">{children}</NoticePopup>;
}
